import {DOCUMENT, isPlatformBrowser} from '@angular/common';
import {Inject, Injectable, InjectionToken, NgZone, OnDestroy, PLATFORM_ID} from '@angular/core';
import {App, AppPlugin} from '@capacitor/app';
import {Capacitor, PluginListenerHandle} from '@capacitor/core';
import {BehaviorSubject} from 'rxjs';

export const APP_LIFECYCLE = new InjectionToken<Pick<AppPlugin, 'getState' | 'addListener'>>('APP_LIFECYCLE', {
  providedIn: 'root', factory: () => App,
});

/** One visibility/native lifecycle observation shared by presence consumers. */
@Injectable({providedIn: 'root'})
export class AppForegroundService implements OnDestroy {
  private readonly state = new BehaviorSubject(false);
  public readonly foreground$ = this.state.asObservable();
  private initialized = false;
  private destroyed = false;
  private nativeActive = false;
  private nativeRevision = 0;
  private nativeListener?: PluginListenerHandle;
  private readonly visibilityChanged = () => this.publish();

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(PLATFORM_ID) private readonly platformId: object,
    private readonly zone: NgZone,
    @Inject(APP_LIFECYCLE) private readonly nativeApp: Pick<AppPlugin, 'getState' | 'addListener'>,
  ) {}

  public get isForeground(): boolean { return this.state.value; }

  public initialize(): void {
    if (this.initialized || this.destroyed || !isPlatformBrowser(this.platformId)) return;
    this.initialized = true;
    const native = Capacitor.isNativePlatform();
    this.nativeActive = !native;
    this.zone.runOutsideAngular(() => this.document.addEventListener('visibilitychange', this.visibilityChanged));
    this.publish();
    if (!native) return;

    const revision = this.nativeRevision;
    void this.nativeApp.addListener('appStateChange', state => {
      if (this.destroyed) return;
      this.nativeRevision++;
      this.nativeActive = state.isActive;
      this.publish();
    }).then(listener => {
      if (this.destroyed) void listener.remove().catch(() => {});
      else this.nativeListener = listener;
    }).catch(() => { /* Browser visibility remains available if the native listener fails. */ });
    void this.nativeApp.getState().then(state => {
      if (this.destroyed || revision !== this.nativeRevision) return;
      this.nativeActive = state.isActive;
      this.publish();
    }).catch(() => {
      if (this.destroyed || revision !== this.nativeRevision) return;
      this.nativeActive = true;
      this.publish();
    });
  }

  private publish(): void {
    if (this.destroyed) return;
    const active = this.nativeActive && this.document.visibilityState === 'visible';
    if (active !== this.state.value) this.zone.run(() => this.state.next(active));
  }

  public ngOnDestroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    if (this.initialized) this.document.removeEventListener('visibilitychange', this.visibilityChanged);
    void this.nativeListener?.remove().catch(() => {});
    this.nativeListener = undefined;
    this.state.complete();
  }
}
